import { type RefObject, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { RefetchConnection } from './useMailboxConnection';

/**
 * The host route's connection `refetch`, wrapped so that keyboard focus is not lost when the re-read
 * settles (BUG-026; WCAG 2.4.3 focus order; design-system §6 "never on `<body>`").
 *
 * A re-read keeps the panel on screen (`useMailboxConnection`'s `refreshing`), but the result it
 * settles into can still replace the focused control: Connect becomes the connected panel, Reconnect
 * clears, Disconnect becomes the not-connected prompt. The browser then drops focus to `<body>` and a
 * keyboard or screen-reader user starts again from the top of the page. So after every re-read made
 * through the returned function has been rendered, and only if focus is now lost and was last inside
 * `scope`, focus moves to `heading` — which carries `tabIndex={-1}`, so it can take focus without
 * joining the Tab order.
 *
 * **Why a wrapper, not a watch on `refreshing`.** A read that answers quickly lands in the same
 * render as the `refreshing: true` that started it, so a component can never observe the refresh
 * begin or end. Counting the re-reads this function has seen settle does not depend on that timing.
 *
 * **Where focus was is tracked continuously, not sampled when the re-read begins.** Disconnect closes
 * its confirm dialog in the same render that starts the re-read, so the focused confirm button is
 * already gone by then. A `focusin` inside `scope` records the element; a `focusout` to an element
 * outside `scope` clears it, because the user moved focus away on purpose.
 *
 * **`scope` must be mounted on the first commit and stay mounted.** The `focusin`/`focusout`
 * listeners attach once, in an effect keyed on the `scope` ref object — which is stable, so the effect
 * never re-runs. A scope element rendered later, or swapped for another, would never get the
 * listeners, and focus inside it would go untracked. Both routes pass their always-rendered
 * `<section>`.
 *
 * **It never steals focus.** Nothing moves if focus last went outside `scope`, if the focused control
 * survived the re-read, or if anything other than `<body>` holds focus when the re-read settles. The
 * first load never moves focus: only a re-read made through the returned function is checked.
 */
export function useRefetchRestoringFocus(
    refetch: RefetchConnection,
    scope: RefObject<HTMLElement | null>,
    heading: RefObject<HTMLElement | null>,
): RefetchConnection {
    /** The last element focused inside `scope`, until focus moves to an element outside it. */
    const lastFocusedInside = useRef<Element | null>(null);
    const [settledRereads, setSettledRereads] = useState(0);
    const checkedRereads = useRef(0);

    useEffect(() => {
        const root = scope.current;
        if (root === null) {
            return;
        }
        const onFocusIn = (event: FocusEvent) => {
            lastFocusedInside.current = event.target instanceof Element ? event.target : null;
        };
        const onFocusOut = (event: FocusEvent) => {
            // `relatedTarget` is null when focus goes nowhere — including when the focused element is
            // removed, which is the case this hook repairs — so only a move to a real element outside
            // the screen clears the record.
            const next = event.relatedTarget;
            if (next instanceof Node && !root.contains(next)) {
                lastFocusedInside.current = null;
            }
        };
        root.addEventListener('focusin', onFocusIn);
        root.addEventListener('focusout', onFocusOut);
        return () => {
            root.removeEventListener('focusin', onFocusIn);
            root.removeEventListener('focusout', onFocusOut);
        };
    }, [scope]);

    const refetchRestoringFocus = useCallback<RefetchConnection>(async () => {
        // `refetch` never rejects and has published its result by the time it resolves, so the
        // render this update causes carries the new result too.
        await refetch();
        setSettledRereads((count) => count + 1);
    }, [refetch]);

    useLayoutEffect(() => {
        if (settledRereads === checkedRereads.current) {
            return;
        }
        checkedRereads.current = settledRereads;
        if (lastFocusedInside.current === null) {
            return;
        }
        if (focusIsLost()) {
            heading.current?.focus();
        }
    }, [settledRereads, heading]);

    return refetchRestoringFocus;
}

/**
 * Nothing holds focus now: `<body>`, or a removed element the environment still reports. Any other
 * element is somewhere focus survived, or somewhere the user put it, and a script leaves it there.
 */
export function focusIsLost(): boolean {
    const active = document.activeElement;
    return active === null || active === document.body || !active.isConnected;
}
